import { z } from 'zod';

import { MESSAGE_TYPES } from './message';

/**
 * 버전 문자열을 숫자 배열로 파싱
 * @param version "1.0.0" 형식의 버전 문자열
 * @returns [major, minor, patch] 숫자 배열
 */
export function parseVersion(version: string): [number, number, number] {
  const parts = version.split('.').map(Number);
  return [parts[0] || 0, parts[1] || 0, parts[2] || 0];
}

/**
 * 버전 비교 함수
 * @param v1 비교할 버전 1
 * @param v2 비교할 버전 2
 * @returns 음수(v1 < v2), 0(v1 = v2), 양수(v1 > v2)
 */
export function compareVersions(v1: string, v2: string): number {
  const [major1, minor1, patch1] = parseVersion(v1);
  const [major2, minor2, patch2] = parseVersion(v2);

  if (major1 !== major2) return major1 - major2;
  if (minor1 !== minor2) return minor1 - minor2;
  return patch1 - patch2;
}

/**
 * 버전이 최소 지원 버전 이상인지 확인
 * @param version 확인할 버전
 * @param minVersion 최소 지원 버전
 * @returns 지원 여부
 */
export function isVersionSupported(version: string | undefined, minVersion: string): boolean {
  if (!version) return true; // 버전이 없으면 레거시 클라이언트로 간주하고 허용
  return compareVersions(version, minVersion) >= 0;
}

// 버전 스키마
const VersionSchema = z
  .string()
  .regex(/^\d+\.\d+\.\d+$/)
  .optional();

// Alert 메시지 스키마
export const AlertMessageSchema = z.object({
  type: z.literal(MESSAGE_TYPES.ALERT),
  version: VersionSchema,
  payload: z.object({
    isOpen: z.boolean(),
  }),
});

// Device Token 응답 메시지 스키마
export const DeviceTokenResponseSchema = z.object({
  type: z.literal(MESSAGE_TYPES.DEVICE_TOKEN),
  version: VersionSchema,
  payload: z.object({
    deviceToken: z.string(),
  }),
});

// 딥링크 네비게이션 메시지 스키마 (App → Web)
export const NavigateMessageSchema = z.object({
  type: z.literal(MESSAGE_TYPES.NAVIGATE),
  version: VersionSchema,
  payload: z.object({
    target: z.enum(['home', 'calendar', 'myPage', 'notification']),
  }),
});

// API 응답 스키마 (WebView에서 수신하는 공통 응답 형식)
export const ApiResponseSchema = z.object({
  resultCode: z.enum(['S', 'F']),
  resultMsg: z.string().optional(),
  data: z.unknown().optional(),
  version: VersionSchema,
});

// 본인인증 응답 스키마 (App → Web)
export const AuthVerificationResponseSchema = z.object({
  resultCode: z.enum(['S', 'F']),
  statusCode: z.union([z.string(), z.number()]).optional(),
  data: z.unknown().optional(),
  errorMessage: z.string().optional(),
  version: VersionSchema,
});

// 수신 메시지 통합 스키마
export const ReceiveMessageSchema = z.union([
  AlertMessageSchema,
  DeviceTokenResponseSchema,
  AuthVerificationResponseSchema,
  NavigateMessageSchema,
]);

// 타입 추출
export type AlertMessage = z.infer<typeof AlertMessageSchema>;
export type DeviceTokenResponse = z.infer<typeof DeviceTokenResponseSchema>;
export type ApiResponse = z.infer<typeof ApiResponseSchema>;
export type ReceiveMessage = z.infer<typeof ReceiveMessageSchema>;

/**
 * WebView 메시지 파싱 및 검증 함수
 * @param data - JSON 파싱된 메시지 데이터
 * @returns 검증된 메시지 또는 null
 */
export function parseReceiveMessage(data: unknown): ReceiveMessage | null {
  const result = ReceiveMessageSchema.safeParse(data);
  if (result.success) {
    return result.data;
  }
  return null;
}

/**
 * Alert 메시지인지 확인
 */
export function isAlertMessage(data: unknown): data is AlertMessage {
  return AlertMessageSchema.safeParse(data).success;
}

/**
 * Device Token 응답 메시지인지 확인
 */
export function isDeviceTokenResponse(data: unknown): data is DeviceTokenResponse {
  return DeviceTokenResponseSchema.safeParse(data).success;
}

/**
 * 유효한 수신 메시지인지 확인하는 타입 가드
 * @param data - 검증할 데이터
 * @returns 유효한 ReceiveMessage인지 여부
 */
export function isValidMessage(data: unknown): data is ReceiveMessage {
  return ReceiveMessageSchema.safeParse(data).success;
}

/**
 * API 응답 메시지인지 확인
 */
export function isApiResponse(data: unknown): data is ApiResponse {
  return ApiResponseSchema.safeParse(data).success;
}

// 본인인증 응답 타입 추출
export type AuthVerificationResponse = z.infer<typeof AuthVerificationResponseSchema>;

/**
 * 본인인증 응답 메시지인지 확인
 */
export function isAuthVerificationResponse(data: unknown): data is AuthVerificationResponse {
  return AuthVerificationResponseSchema.safeParse(data).success;
}

// 네비게이션 메시지 타입 추출
export type NavigateMessage = z.infer<typeof NavigateMessageSchema>;

/**
 * Navigate 메시지인지 확인
 */
export function isNavigateMessage(data: unknown): data is NavigateMessage {
  return NavigateMessageSchema.safeParse(data).success;
}
